import { Journals } from '../../../../types/Journals.types';
import { Box, Card, Typography, Stack, Divider, Link } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { MasterlistJournalCheck } from '../../../../types/masterlistCheck.types';
import { ErrorBadge } from './ErrorBadge';
import { MasterlistStatus } from './MasterlistStatus';
import { checkFor } from './masterlistCheck';
import {
  isMissingColumn,
  isMissingData,
  journalCellMissing,
  journalDescribedBy,
  journalRowId,
  masterlistStatusId,
  missingDataId,
} from './utils';

interface JournalsTableMobileProps {
  journals: Journals[];
  onJournalClick: (journal: Journals) => void;
  /** Set when the masterlist status is shown; journals without a check get no status line. */
  checksByJournalId?: Map<string, MasterlistJournalCheck>;
}

export function JournalsTableMobile({
  journals,
  onJournalClick,
  checksByJournalId,
}: JournalsTableMobileProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 24, height: '640px' }}>
      <Virtuoso
        style={{ height: '100%' }}
        data={journals}
        itemContent={(index, journal) => {
          const rowId = journalRowId('mobile', journal, index);
          const check = checkFor(checksByJournalId, journal);
          const describedBy = journalDescribedBy(rowId, journal, !!check);
          const url = journal.url?.trim();
          const cellMissing = journalCellMissing(journal);
          const isNameMissing = cellMissing === 'name';
          const missingInfo = cellMissing
            ? (t(`agreements.journalTable.missing.${cellMissing}`) as string)
            : '';

          const journalNameElement = isNameMissing ? null : journal.name;
          const journalUrl = url ? (
            <Link
              href={url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {journalNameElement}
            </Link>
          ) : (
            <span>{journalNameElement}</span>
          );

          const journalLabel =
            journal.name || journal.issn_e || `Journal ${index + 1}`;

          return (
            <Box sx={{ mb: 12 }} key={journal.issn_e || journal._id || index}>
              <Card
                variant="primary"
                // A named group: aria-label and aria-describedby need a role to be read, and a
                // button would flatten the journal's link inside the card.
                role="group"
                tabIndex={0}
                aria-label={
                  t('agreements.openJournal', {
                    name: journalLabel,
                    defaultValue: `Open journal, ${journalLabel}`,
                  }) as string
                }
                aria-describedby={describedBy}
                onClick={() => onJournalClick(journal)}
                onKeyDown={(e) => {
                  // Keys on the journal's link are the link's: Enter there follows it.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onJournalClick(journal);
                  }
                }}
                sx={{
                  backgroundColor: isMissingData(journal)
                    ? 'var(--color-very-pale-orange)'
                    : 'var(--color-white)',
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.journal')}{' '}
                      <span aria-hidden="true">*</span>
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {missingInfo ? (
                        <ErrorBadge
                          title={missingInfo}
                          id={missingDataId(rowId, 'name')}
                        >
                          {journalNameElement}
                        </ErrorBadge>
                      ) : (
                        journalUrl
                      )}
                    </Typography>
                  </Box>
                  <Divider />
                  {check && (
                    <>
                      <MasterlistStatus
                        id={masterlistStatusId(rowId)}
                        check={check}
                        compact
                      />
                      <Divider />
                    </>
                  )}
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.imprint')}
                    </Typography>
                    <Typography variant="body1">
                      {journal.imprint || '-'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.journalTable.payment')}
                    </Typography>
                    <Typography variant="body1">
                      {isMissingColumn(journal, 'payment') ? (
                        <ErrorBadge
                          title={
                            t(
                              'agreements.journalTable.missing.payment'
                            ) as string
                          }
                          id={missingDataId(rowId, 'payment')}
                        />
                      ) : (
                        journal.payment || '-'
                      )}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.journalTable.licenses')}
                    </Typography>
                    <Typography variant="body1">
                      {isMissingColumn(journal, 'licenses') ? (
                        <ErrorBadge
                          title={
                            t(
                              'agreements.journalTable.missing.licenses'
                            ) as string
                          }
                          id={missingDataId(rowId, 'licenses')}
                        />
                      ) : journal.licenses ? (
                        journal.licenses.join(', ')
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      <>
                        {t('agreements.journalTable.publishingModel')}{' '}
                        <span aria-hidden="true">*</span>
                      </>
                    </Typography>
                    <Typography variant="body1">
                      {isMissingColumn(journal, 'publishing_model') ? (
                        <ErrorBadge
                          title={
                            t(
                              'agreements.journalTable.missing.publishingModel'
                            ) as string
                          }
                          id={missingDataId(rowId, 'publishing_model')}
                        />
                      ) : (
                        journal.publishing_model || '-'
                      )}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.issne')}
                    </Typography>
                    <Typography variant="body1">
                      {journal.issn_e || '-'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.issnp')}
                    </Typography>
                    <Typography variant="body1">
                      {journal.issn_p || '-'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption">
                      {t('agreements.journalTable.subjects')}
                    </Typography>
                    <Typography variant="body1">
                      {journal.subjects || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Box>
          );
        }}
      />
    </Box>
  );
}
